import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useSEO } from "../hooks/useSEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Brain, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { PasswordInput } from "../components/PasswordInput";

export default function RegisterPage() {
  const { register, t, language } = useApp();
  useSEO({
    title: language === "es" ? "Crea tu cuenta · BScanIA — Mentor IA para decisiones" : "Sign up · BScanIA — AI Mentor for Decisions",
    description: language === "es"
      ? "Empieza gratis con BScanIA. Tu mentor personal con IA para decisiones difíciles, claridad emocional y crecimiento profesional. Bilingüe ES/EN."
      : "Start free with BScanIA. Your personal AI mentor for hard decisions, emotional clarity and professional growth. Bilingual EN/ES.",
    canonical: "https://bscan.app/register",
    lang: language,
  });
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("auth.password_too_short"));
      return;
    }
    if (!acceptTerms) {
      toast.error(language === "es"
        ? "Debes aceptar los Términos, la Política de Privacidad y el Aviso de Bienestar para continuar."
        : "You must accept the Terms, Privacy Policy and Wellbeing Notice to continue.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, fullName, { accept_terms: true, accept_terms_version: "2026-02-v1" });
      toast.success(t("auth.account_created"));
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.registration_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#7C3AED]/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-10" data-testid="register-logo">
          <img
            src="/brand-logo.png"
            alt="BScanIA"
            className="h-28 w-auto"
          />
        </Link>

        <div className="bg-[#121214] border border-white/10 rounded-2xl p-8">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-2">{t("auth.register_tag")}</div>
          <h1 className="font-display font-bold text-3xl mb-1">{t("auth.register_title")}</h1>
          <p className="text-white/60 mb-8">{t("auth.register_subtitle")}</p>

          <GoogleSignInButton />

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{t("auth.or_divider")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="fullName" className="text-white/80 mb-2 block">{t("auth.full_name")}</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="bg-[#0A0A0A] border-white/15 text-white focus:border-[#006CFF]"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-white/80 mb-2 block">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#0A0A0A] border-white/15 text-white focus:border-[#006CFF]"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80 mb-2 block">{t("auth.password")}</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="flex h-10 w-full rounded-md border border-white/15 bg-[#0A0A0A] px-3 py-2 text-base text-white focus:border-[#006CFF] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                data-testid="register-password-input"
                testIdToggle="register-password-toggle"
              />
            </div>
            {/* Mandatory legal acceptance — checkbox blocks submit until checked */}
            <div className="p-4 rounded-xl bg-[#FFB800]/8 border border-[#FFB800]/25">
              <label className="flex items-start gap-3 cursor-pointer select-none" htmlFor="acceptTerms">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 accent-[#006CFF] cursor-pointer flex-shrink-0"
                  data-testid="register-accept-terms-checkbox"
                />
                <div className="flex-1 text-xs leading-relaxed text-white/75">
                  <div className="flex items-center gap-1.5 mb-1.5 text-[#FFB800] font-mono uppercase tracking-[0.15em] text-[10px]">
                    <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2} />
                    {language === "es" ? "Importante — léelo" : "Important — please read"}
                  </div>
                  {language === "es" ? (
                    <>
                      He leído y acepto los{" "}
                      <Link to="/terms" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Términos de Servicio</Link>,
                      la{" "}
                      <Link to="/privacy" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Política de Privacidad</Link>,
                      y entiendo que <strong className="text-white">BScanIA NO es terapia, ni atención médica, ni asesoría profesional</strong>.
                      Es una herramienta de orientación con IA. Si estoy en crisis, llamaré a una línea de emergencia (en EE.UU.: 988).
                    </>
                  ) : (
                    <>
                      I have read and accept the{" "}
                      <Link to="/terms" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Terms of Service</Link>,
                      the{" "}
                      <Link to="/privacy" target="_blank" className="text-[#006CFF] underline hover:text-[#3388FF]">Privacy Policy</Link>,
                      and I understand that <strong className="text-white">BScanIA is NOT therapy, medical care, or professional advice</strong>.
                      It's an AI-powered guidance tool. If I'm in crisis, I'll call an emergency line (US: 988).
                    </>
                  )}
                </div>
              </label>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#006CFF] hover:bg-[#0052CC] text-white py-6 disabled:opacity-50" data-testid="register-submit-btn">
              {loading ? t("common.loading") : t("auth.register_btn")}
            </Button>
          </form>

          <p className="mt-6 text-center text-white/60 text-sm">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-[#006CFF] hover:underline" data-testid="register-to-login-link">
              {t("auth.sign_in")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
